import React, { useCallback, useEffect, useState } from "react";
import { Copy, Wallet, Gift, TrendingUp, Users2, Share2, MessageCircle, Mail, Twitter, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * Agent portal — visible only to users linked to a Referrer record.
 * Shows earnings, tier, share links + marketing copy templates.
 */
export default function AgentPortal() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/agent/me"),
        api.get("/agent/stats"),
      ]);
      setMe(m.data);
      setStats(s.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "You don't have a referrer profile yet. Ask an admin to create one and link it to your account.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-4" data-testid="agent-empty-state">
        <div className="w-14 h-14 mx-auto rounded-full bg-secondary/15 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-secondary" />
        </div>
        <h1 className="font-display text-3xl font-light">Agent portal</h1>
        <p className="text-sm text-muted-foreground">{err}</p>
        <Link to="/app"><Button variant="outline">Back to dashboard</Button></Link>
      </div>
    );
  }
  if (!me || !stats) {
    return <div className="p-10 text-center text-sm text-muted-foreground">Loading your dashboard…</div>;
  }

  const shareUrl = `${window.location.origin}/signup?ref=${me.code}`;
  const cs = me.commission_structure || {};

  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copied"));

  const shareText = `I'm a Treasurescare partner — sign up with my code ${me.code} and unlock a warm welcome discount on your gadget protection: ${shareUrl}`;

  const shareVia = (channel) => {
    const enc = encodeURIComponent(shareText);
    const url = encodeURIComponent(shareUrl);
    const links = {
      whatsapp: `https://wa.me/?text=${enc}`,
      email: `mailto:?subject=${encodeURIComponent("Treasurescare — gadget protection")}&body=${enc}`,
      x: `https://x.com/intent/tweet?text=${enc}&url=${url}`,
    };
    if (links[channel]) window.open(links[channel], "_blank", "noopener,noreferrer");
  };

  const templates = [
    {
      title: "Short text / SMS",
      body: `Cracked your screen? I'm with Treasurescare — full gadget cover from £4.99/mo. Sign up with code ${me.code} for a welcome discount: ${shareUrl}`,
    },
    {
      title: "Email pitch",
      body: `Hi [Name],\n\nQuick one — I'm a partner at Treasurescare, the UK gadget-agnostic protection plan (phones, laptops, tablets, watches).\n\nWith code ${me.code} you'll get a welcome discount on your first payment. Link: ${shareUrl}\n\nHappy to answer any questions!\n\n— ${me.name}`,
    },
    {
      title: "LinkedIn / social",
      body: `We buy £1,000+ gadgets. We insure them for pennies. That's my pitch. If you want protection that pays out fast (AI damage assessment + 24h repair), use code ${me.code} at ${shareUrl}.`,
    },
  ];

  return (
    <div className="space-y-8" data-testid="agent-portal">
      <div>
        <div className="overline text-secondary">Partner portal</div>
        <h1 className="mt-1 font-display text-4xl font-light tracking-tight">Hello, {me.name.split(" ")[0]}.</h1>
        <p className="mt-2 text-muted-foreground text-sm">
          Track your pipeline, share your kit, and see every penny you've earned in one place.
        </p>
        {me.status !== "active" && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/15 text-yellow-800 text-xs" data-testid="agent-status-banner">
            <span className="font-semibold capitalize">{me.status}</span>
            <span>· Your referrer profile isn't active right now — new signups won't earn commission. Contact your admin.</span>
          </div>
        )}
      </div>

      {/* Earnings stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <EarningsCard icon={<TrendingUp className="w-4 h-4" />} label="Pending" money={stats.pending.total_gbp} count={stats.pending.count} tone="yellow" />
        <EarningsCard icon={<Gift className="w-4 h-4" />} label="Approved" money={stats.approved.total_gbp} count={stats.approved.count} tone="blue" />
        <EarningsCard icon={<Wallet className="w-4 h-4" />} label="Paid out" money={stats.paid.total_gbp} count={stats.paid.count} tone="teal" />
        <EarningsCard icon={<Users2 className="w-4 h-4" />} label="Clawed back" money={stats.clawed_back.total_gbp} count={stats.clawed_back.count} tone="muted" />
      </div>

      {/* Commission plan snapshot */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="overline text-secondary">Your commission plan</div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <PlanCell label="First-payment %" value={`${cs.first_period_pct || 0}%`} />
          <PlanCell label="Recurring %" value={`${cs.recurring_pct || 0}%`} />
          <PlanCell label="Customer gets" value={cs.customer_discount_value ? `${cs.customer_discount_value} ${cs.customer_discount_type || ""}` : "—"} />
          <PlanCell label="Clawback window" value={`${cs.clawback_days || 0} days`} />
        </div>
      </div>

      {/* Share block */}
      <div className="bg-primary text-primary-foreground rounded-2xl p-8 shadow-teal-glow relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative">
          <div className="overline text-secondary">Your unique code</div>
          <div className="mt-2 font-display text-4xl tracking-tight" data-testid="agent-code">{me.code}</div>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Input readOnly value={shareUrl} className="font-mono text-xs bg-white/10 border-white/20 text-primary-foreground placeholder:text-white/50" data-testid="agent-share-link" />
            <Button variant="outline" className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20" onClick={() => copy(shareUrl)} data-testid="agent-copy-link">
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
            <Button className="bg-secondary text-secondary-foreground" onClick={() => shareVia("whatsapp")} data-testid="agent-share-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => shareVia("email")}><Mail className="w-3.5 h-3.5 mr-1.5" /> Email</Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => shareVia("x")}><Twitter className="w-3.5 h-3.5 mr-1.5" /> X</Button>
          </div>
        </div>
      </div>

      {/* Marketing kit */}
      <div>
        <div className="overline text-secondary">Marketing kit</div>
        <h2 className="mt-1 font-display text-2xl">Copy-paste templates</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {templates.map((t, i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-5" data-testid={`marketing-template-${i}`}>
              <div className="font-display text-lg">{t.title}</div>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground font-mono max-h-40 overflow-auto">{t.body}</pre>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => copy(t.body)}>
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Recent commissions */}
      <div>
        <div className="overline text-secondary">Latest activity</div>
        <div className="mt-3 bg-card border border-border rounded-2xl overflow-hidden">
          {(stats.recent || []).length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">No commissions yet — share your link to get started.</div>
          )}
          {(stats.recent || []).map((c) => (
            <div key={c.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 text-sm" data-testid={`agent-commission-${c.id}`}>
              <div className="col-span-5 font-medium">£{c.amount_gbp.toFixed(2)} <span className="text-xs text-muted-foreground">· {c.period}</span></div>
              <div className="col-span-4 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</div>
              <div className="col-span-3 text-right">
                <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${
                  c.status === "paid" ? "bg-primary/10 text-primary" :
                  c.status === "approved" ? "bg-blue-500/15 text-blue-700" :
                  c.status === "pending" ? "bg-yellow-500/15 text-yellow-700" :
                  "bg-destructive/15 text-destructive"
                }`}>{c.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EarningsCard({ icon, label, money, count, tone = "muted" }) {
  const toneMap = {
    yellow: "bg-yellow-500/10 text-yellow-800",
    blue: "bg-blue-500/10 text-blue-800",
    teal: "bg-primary/10 text-primary",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${toneMap[tone]}`}>{icon}{label}</div>
      <div className="mt-2 font-display text-2xl">£{(money || 0).toFixed(2)}</div>
      <div className="text-xs text-muted-foreground">{count || 0} commissions</div>
    </div>
  );
}

function PlanCell({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl">{value}</div>
    </div>
  );
}
